import React from "react";
import FormFactory from "../FormFactory";
import ToastSuccess from "../ToastSuccess";
import ReceiptsFormDialog from "../../pages/receipts/form/components/FormDialog";

/**
 * FormDialog – unified form dialog for both schema-driven, receipts, and custom form content.
 *
 * Mode 1 – Receipts (uses FormDialog from receipts and its Form, NetSummary, utils):
 *   Pass: variant="receipts" plus receipts props (onSubmit, suppliers, banks, object, onCancel, setState, receipts, collection, supplier, supplierField, ...).
 *
 * Mode 2 – Schema-driven (like RelationDialog create form):
 *   Pass: schema, object, onSave, onBack, findObject, saveObject, saveImage?, saveFile?, schemas?
 *   Renders FormFactory from schema; submit calls saveObject.execute(schema.collection, change) then onSave(object).
 *
 * Mode 3 – Custom content:
 *   Pass: children (form body), onSubmit, onCancel, optional title, submitLabel, cancelLabel, disabled, object (for "Update" vs "Save").
 *   Renders your form content with consistent card layout and footer buttons.
 *
 * Common props:
 *   title – section header (default: schema mode "Create {schema.label}", custom "Form")
 *   titleClassName – e.g. "text-primary fw-bold"
 *   showTitle – default true
 *   footer – custom footer element (overrides default buttons)
 *   showFooter – default true
 *   toast – { show: boolean, message: string } to show ToastSuccess in the same position as other FormDialogs
 *   className – extra class on the card wrapper
 */
function FormDialog({
  // Receipts mode – delegates to receipts FormDialog and its components (Form, NetSummary, utils)
  variant,
  // Schema mode
  schema,
  object,
  onSave,
  onBack,
  findObject,
  saveObject,
  saveImage,
  saveFile,
  schemas,
  // Custom mode
  children,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
  disabled,
  // Layout
  title,
  titleClassName = "",
  showTitle = true,
  footer,
  showFooter = true,
  toast,
  className = "",
  // FormFactory / form props + receipts props (suppliers, banks, setState, receipts, collection, supplier, supplierField, etc.)
  ...props
}) {
  // Hooks must be called unconditionally (before any early return)
  const [change, setChange] = React.useState({});

  if (variant === "receipts") {
    return (
      <ReceiptsFormDialog
        onSubmit={onSubmit}
        object={object}
        onCancel={onCancel}
        suppliers={props.suppliers}
        banks={props.banks}
        setState={props.setState}
        receipts={props.receipts}
        collection={props.collection}
        supplier={props.supplier}
        supplierField={props.supplierField}
        {...props}
      />
    );
  }

  const isSchemaMode = Boolean(schema && !children);
  const effectiveTitle =
    title != null
      ? title
      : isSchemaMode
        ? `Create ${schema?.label || schema?.collection || "Item"}`
        : "Form";

  const handleSchemaChange = (value, field) => {
    setChange((prev) => ({ ...prev, [field]: value }));
  };

  const handleSchemaSubmit = async (e) => {
    e.preventDefault();
    try {
      const saved = await saveObject.execute(schema.collection, change);
      onSave?.(saved);
    } catch (err) {
      console.error("FormDialog save failed", err);
    }
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    onSubmit?.(e);
  };

  const handleSecondary = () => {
    if (isSchemaMode) onBack?.();
    else onCancel?.();
  };

  const defaultSubmitLabel = object?.id ? "Update" : "Save";
  const defaultCancelLabel = isSchemaMode ? "GO BACK" : "Cancel";

  const effectiveSubmitLabel = submitLabel ?? defaultSubmitLabel;
  const effectiveCancelLabel = cancelLabel ?? defaultCancelLabel;

  const toastShow = toast?.show ?? false;
  const toastMessage = toast?.message ?? "";

  return (
    <>
      <div className={`m-0 card bg-white p-3 ${className}`.trim()}>
        <div className="d-flex flex-column">
          {showTitle && effectiveTitle && (
            <div style={{ borderBottom: "1px solid gray" }}>
              <p
                className={`mb-1 ${titleClassName}`.trim()}
                style={{ fontSize: "0.9rem" }}
              >
                {effectiveTitle}
              </p>
            </div>
          )}

          {isSchemaMode ? (
            <form onSubmit={handleSchemaSubmit}>
              <div className="row g-2 py-2">
                <FormFactory
                  className="col-md-6"
                  schema={schema}
                  object={object}
                  onChange={handleSchemaChange}
                  findObject={findObject}
                  saveObject={saveObject}
                  saveFile={saveFile}
                  saveImage={saveImage}
                  schemas={schemas}
                  {...props}
                />
              </div>
              {showFooter && !footer && (
                <div className="text-end mt-3">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={disabled}
                  >
                    <i className="bi bi-plus me-2" />
                    SAVE AND ASSOCIATE
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm ms-2"
                    onClick={handleSecondary}
                  >
                    {effectiveCancelLabel}
                  </button>
                </div>
              )}
            </form>
          ) : (
            <>
              <form onSubmit={handleCustomSubmit}>
                <div className="d-flex flex-column gap-2 my-2">{children}</div>
                {showFooter && !footer && (
                  <div className="d-flex gap-2 mt-3">
                    <button
                      type="button"
                      className="btn card w-100"
                      onClick={handleSecondary}
                      style={{ backgroundColor: "#D1D1D1" }}
                    >
                      {effectiveCancelLabel}
                    </button>
                    <button
                      type="submit"
                      className="w-100 rounded-2 text-white"
                      style={{ backgroundColor: "#14213D" }}
                      disabled={disabled}
                    >
                      {effectiveSubmitLabel}
                    </button>
                  </div>
                )}
              </form>
              {footer}
            </>
          )}
        </div>
      </div>

      {(toastShow || toastMessage) && (
        <div
          className="toast-container position-fixed"
          style={{
            transform: "translate(-50%,-50%)",
            top: "10%",
            left: "50%",
          }}
        >
          <ToastSuccess show={toastShow} message={toastMessage} />
        </div>
      )}
    </>
  );
}

export default FormDialog;
